import React from "react";
import { motion } from "framer-motion";
import { Calendar, Users, CheckCircle2, Building } from "lucide-react";
import { useNavigate } from "react-router";

const FlexOfficeTimeline = () => {

    const navigate = useNavigate();
    const steps = [
        {
            id: "room",
            icon: <Building className="w-8 h-8" />,
            title: "Choose Your Space",
            description:
                "Select a conference room or workstation that fits your needs.",
            gradient: "from-blue-400 to-blue-600",
        },
        {
            id: "date",
            icon: <Calendar className="w-8 h-8" />,
            title: "Pick Date & Duration",
            description:
                "Book by the hour, day, or week—total flexibility.",
            gradient: "from-emerald-400 to-emerald-600",
        },
        {
            id: "capacity",
            icon: <Users className="w-8 h-8" />,
            title: "Set Capacity",
            description:
                "Add the number of attendees to ensure you have the perfect setup.",
            gradient: "from-violet-400 to-violet-600",
        },
        {
            id: "confirm",
            icon: <CheckCircle2 className="w-8 h-8" />,
            title: "Confirm & Go",
            description:
                "Review your details and confirm your booking instantly.",
            gradient: "from-amber-400 to-amber-600",
        },
    ];

    const handleNavigate = () => {
        navigate("/flex-home");
    }

    return (
        <section
            id="timeline"
            className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 sm:py-24 lg:py-32 relative overflow-hidden"
            style={{ fontFamily: "var(--flex-font, 'Inter', sans-serif)" }}
        >
            {/* Glowing Backgrounds */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-transparent" />
            <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-bl from-blue-500/20 to-transparent rounded-full blur-3xl -translate-y-20 translate-x-20" />

            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <span className="inline-block bg-white/8 backdrop-blur-md text-blue-300 px-4 py-2 text-sm font-semibold mb-6 border border-white/15 rounded-full">
                            How It Works
                        </span>
                        <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
                            Book Your Workspace in
                            <br />
                            <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                                4 Simple Steps
                            </span>
                        </h2>
                        {/* <p className="text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed font-medium">
                            Our booking system is designed to make your experience fast,
                            intuitive, and hassle-free.
                        </p> */}
                    </motion.div>
                </div>

                {/* Steps Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {steps.map((step, index) => (
                        <motion.div
                            key={step.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className="group relative"
                        >
                            {/* Card */}
                            <div className="bg-white/[0.06] backdrop-blur-md rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden border border-white/10 h-full transform hover:-translate-y-2 hover:bg-white/[0.1] hover:border-white/20">
                                {/* Gradient Top Bar */}
                                <div
                                    className={`h-2 bg-gradient-to-r ${step.gradient}`}
                                ></div>

                                {/* Card Content */}
                                <div className="p-8 relative z-10 text-center">
                                    {/* Icon Container */}
                                    <div
                                        className={`mb-6 mx-auto w-16 h-16 bg-gradient-to-r ${step.gradient} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}
                                    >
                                        <div className="text-white">{step.icon}</div>
                                    </div>

                                    {/* Step Number */}
                                    <div className="mb-4">
                                        <span className="inline-flex items-center justify-center w-8 h-8 bg-white/10 backdrop-blur-sm text-blue-300 font-bold text-sm rounded-full border border-white/10">
                                            {index + 1}
                                        </span>
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-slate-100 transition-colors duration-200">
                                        {step.title}
                                    </h3>

                                    {/* Description */}
                                    <p className="text-slate-300 leading-relaxed font-medium">
                                        {step.description}
                                    </p>

                                    {/* Hover Gradient Line */}
                                    <div
                                        className={`absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r ${step.gradient} group-hover:w-full transition-all duration-300`}
                                    ></div>
                                </div>

                                {/* Hover Background Accent */}
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-300">
                                    <div
                                        className={`absolute inset-0 bg-gradient-to-br ${step.gradient}`}
                                    ></div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* CTA Button */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    viewport={{ once: true }}
                    className="text-center mt-16"
                    onClick={handleNavigate}



                >
                    <button className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 font-semibold rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-800 hover:shadow-blue-500/40 transition-all duration-200 transform hover:scale-105">
                        Start Booking Now →
                    </button>
                </motion.div>
            </div>
        </section>
    );
};

export default FlexOfficeTimeline;
